import { useAppDispatch, useAppSelector } from 'hooks';
import { useEffect } from 'react';
import {
  selectShouldFocusReceivedMessage,
  resetShouldFocusReceivedMessage,
} from 'reducers/workflows';

function focusFirstBotMessage() {
  document.querySelector<HTMLDivElement>('.js-bot-message')?.focus();
}

function focusReceivedMessage() {
  const userMessages = document.querySelectorAll<HTMLDivElement>(
    '.js-user-message, .js-sent-dynamic-card-message',
  );

  const lastUserMessage = userMessages[userMessages.length - 1];

  if (!lastUserMessage) {
    focusFirstBotMessage();

    return;
  }

  let wrapperOfFirstBotMessageInLastBatch = lastUserMessage.closest(
    '.js-generic-step-component',
  )?.nextElementSibling;

  // The found `wrapperOfFirstBotMessageInLastBatch` can actually be empty,
  // so keep looking for a non-empty one:
  while (
    !wrapperOfFirstBotMessageInLastBatch?.querySelector('.js-bot-message')
  ) {
    wrapperOfFirstBotMessageInLastBatch =
      wrapperOfFirstBotMessageInLastBatch?.nextElementSibling;
  }

  if (!wrapperOfFirstBotMessageInLastBatch) {
    return;
  }

  wrapperOfFirstBotMessageInLastBatch
    .querySelector<HTMLDivElement>('.js-bot-message, .js-first-dynamic-card')
    ?.focus();
}

export default function useFocusReceivedMessage(): void {
  const dispatch = useAppDispatch();
  const shouldFocusReceivedMessage = useAppSelector(
    selectShouldFocusReceivedMessage,
  );

  // Reset `shouldFocusReceivedMessage` on page refresh:
  useEffect(() => {
    dispatch(resetShouldFocusReceivedMessage());
  }, [dispatch]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (
        event.key === 'Tab' &&
        !event.shiftKey &&
        shouldFocusReceivedMessage
      ) {
        event.preventDefault();
        focusReceivedMessage();
      }

      // Reset `shouldFocusReceivedMessage` on ANY key press:
      if (shouldFocusReceivedMessage) {
        dispatch(resetShouldFocusReceivedMessage());
      }
    };

    // `document.body` can be `null`
    document.body?.addEventListener('keydown', listener);

    return () => {
      // `document.body` can be `null`
      document.body?.removeEventListener('keydown', listener);
    };
  }, [dispatch, shouldFocusReceivedMessage]);
}
